import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import './App.css';

import { Navigation } from './components/navigation/navigation';
import AuthenticationPage from './pages/authentication-page/authentication-page';
import MapPage from './pages/map-page/map-page';
import { LocationHistoryPage } from './pages/location-history-page/location-history-page';
import { ShoppingListPage } from './pages/shopping-list/shopping-list';
import { ManageYourTeam } from './pages/manage-your-team-page/manage-your-team-page';
import { NotificationPage } from './pages/notification-page/notification-page';
import { PartLookUp } from './pages/part-lookup/part-lookup';
import { AddBulkAsset } from './pages/add-bulk-asset/add-bulk-asset';
import { UploadAssets } from './pages/upload-assets/upload-assets';

import { SELECTED_ASSET } from './constants/bucket.contants';
import { IDLE_INTERVAL, KEYDOWN, TIMEOUT } from './constants/idle.constants';
import { AppConfigContext } from './contexts/app-config.context';
import { GlobalErrorProvider, useGlobalError } from './contexts/global-error.context';
import { GlobalErrorInitializer } from './components/global-error/with-global-error';
import { useAppConfigStore } from './hooks/app-config-store.hook';
import { logout } from './utils/logout.util';
import { routePath } from './constants/navigation.constants';
import { ACCESS_TKN_KEY, ROLES, StorageService } from './services/storage_service';

import { Footer } from "@weirgroup/weir-dls";
import { footerLinks } from './constants/footer.constants';
import ErrorPage from './pages/error-page/error-page';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from './hooks/use-darkmode';
import AssetDetailPage from './pages/asset-detail-page/asset-detail-page';
import { queryClient } from './utils/query-client';
import { getPageAnalyticsByPath } from './constants/analytics.constants';
import { LoadingSpinner } from './components/loading-spinner';
import firebase from './utils/firebase.util';
import { logEvent } from 'firebase/analytics';

        
// AnalyticsTracker component to log page title and screen class on route change

interface RoutesType {
    id: number
    path: string
    element: JSX.Element,
    darkMode?: boolean
}

const getPageAnalytics = (pathname: string) => {
    // Remove trailing slash for consistency
    const cleanPath = pathname.replace(/\/$/, '');
    return getPageAnalyticsByPath(cleanPath, routePath);
};

const AnalyticsTracker = () => {
    const location = useLocation();
    const { showError } = useGlobalError();
    useEffect(() => {
        try {
            const { page_title, screen_class } = getPageAnalytics(location.pathname);
            document.title = `ESCO Track Pro ${page_title}`;
            const { analytics } = firebase();
            logEvent(analytics, 'page_view', {
                page_title: `Web: ${page_title}`,
                screen_name: `Web: ${screen_class}`,
                page_path: location.pathname,
            });
            console.log('triggered analytics event for page:', page_title);
        } catch (e) {
            // Analytics might not be initialized in some environments
            showError(String(e), 'AnalyticsTracker');
        }
    }, [location]);
    return null;
};

function App() {
    const { t } = useTranslation();
    const [showMapPage, setShowMapPage] = useState<boolean>(false)
    const [selectedAsset, setSelectedAsset] = useState<any>()
    const [isInitialized, setIsInitialized] = useState<boolean>(false)

    const appConfigStore = useAppConfigStore()

    const timeout = TIMEOUT
    const [elapsed, setElapsed] = useState(0)
    const [isIdle, setIsIdle] = useState(false)
    
    const { reset, getElapsedTime } = useIdleTimer({
        timeout,
        onActive: () => setIsIdle(false),
        onIdle: () => setIsIdle(true),
    })
    const {darkMode} = useDarkMode()
    
    // Check if user is already authenticated on app load
     useEffect(() => {
        const checkAuthStatus = async () => {
            try {
                const accessToken = StorageService.getItem(ACCESS_TKN_KEY);
                const roles = StorageService.getItem(ROLES);
                
                if (accessToken && roles) {
                    setShowMapPage(true);
                }
            } catch (error) {
                // If token validation fails, clear storage and stay on auth page
                console.log('Token validation failed, user needs to re-authenticate');
                StorageService.clear();
                sessionStorage.clear();
            } finally {
                firebase();
                setIsInitialized(true);
            }
        };

        checkAuthStatus();
    }, []);

    useEffect(() => {
        if (selectedAsset != null) {
            // Only store minimal fields to avoid quota errors
            const minimalAsset = {
                asset_id: selectedAsset.asset_id,
                tag_id: selectedAsset.tag_id,
                serial_number: selectedAsset.serial_number,
                make: selectedAsset.make,
                model: selectedAsset.model
            };
            sessionStorage.setItem(SELECTED_ASSET, JSON.stringify(minimalAsset));
        }
    }, [selectedAsset])

    useEffect(() => {
        setSelectedAsset(null)
        const keyDownHandler = () => {
            setIsIdle(false)
            reset()
        }
        document.addEventListener(KEYDOWN, keyDownHandler)

        setElapsed(getElapsedTime())
        setInterval(() => {
            setElapsed(getElapsedTime())
        }, 1000)
    }, [])

    useEffect(() => {
        reset()
    }, [isIdle])

    useEffect(() => {
        if (elapsed >= IDLE_INTERVAL && isIdle) {
            reset()
            logout()
        }
    }, [elapsed])
    const routes: RoutesType[] = [
        {
            id: 1,
            path: '/',
            element: <Navigate to={routePath.HOMEPAGE} replace />,
        },
        {
            id: 2,
            path: routePath.HOMEPAGE,
            element: (
                <MapPage
                    selectedAsset={selectedAsset}
                    setSelectedAsset={setSelectedAsset}
                />
            ),
        },
        {
            id: 3,
            path: routePath.LOCATION_HISTORY,
            element: <LocationHistoryPage selectedAsset={selectedAsset} />,
        },
        { id: 4, path: routePath.SHOPPING_LIST, element: <ShoppingListPage /> },
        { id: 5, path: routePath.MANAGE_YOUR_TEAM, element: <ManageYourTeam /> },
        { id: 6, path: routePath.NOTIFICATION, element: <NotificationPage /> },
        { id: 7, path: routePath.SERIAL_LOOKUP, element: <PartLookUp /> },
        { id: 8, path: routePath.ADD_BULK_ASSET, element: <AddBulkAsset /> },
        { id: 9, path: routePath.UPLOAD_ASSETS, element: <UploadAssets /> },
        { id: 10, path: '*', element: <ErrorPage /> },
        { id: 11, path: routePath.ASSET_DETAIL, element: <AssetDetailPage /> },
    ]
    return (
        <>
            {!isInitialized ? (
                // Show loading while checking auth status
                   <LoadingSpinner darkMode={darkMode} />
            ) : (
                <div className="page-transition">
                    {showMapPage ? (
                        <GlobalErrorProvider>
                            <QueryClientProvider client={queryClient}>
                                <AppConfigContext.Provider value={appConfigStore}>
                                    <GlobalErrorInitializer />
                                    <AnalyticsTracker />
                                    <div className="app-layout">
                                        <div className="app-header">
                                            <Navigation isAuthenticated />
                                        </div>
                                        <div className="app-content">
                                            <Routes>
                                                {routes.map((route: RoutesType) => (
                                                    <Route
                                                        path={route.path}
                                                        element={route.element}
                                                        key={route.id}
                                                    />
                                                ))}
                                            </Routes>
                                        </div>
                                        <div className="app-footer">
                                            <Footer
                                                darkMode={darkMode}
                                                logoName={"WeirLogo"}
                                                NavigationLinks={footerLinks}
                                                InformationText={t("siteInformation")}
                                                CopyrightText={t("copyrightText")}
                                            />
                                        </div>
                                    </div>
                                </AppConfigContext.Provider>
                            </QueryClientProvider>
                        </GlobalErrorProvider>
                    ) : (
                        <GlobalErrorProvider>
                            <GlobalErrorInitializer />
                            <AuthenticationPage setShowMapPage={setShowMapPage} />
                        </GlobalErrorProvider>
                    )}
                </div>
            )}
        </>
    )
}

export default App
